// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * PricePAL Assistant - Floating AI Chat Widget
 * Available on every page with FAQ integration
 * Voice and text input, context-aware help
 */
const PricePALAssistant = ({ userId, currentPage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'faq'
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [faqCategories, setFaqCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryFaqs, setCategoryFaqs] = useState([]);

  // Draggable bubble state
  const [bubblePos, setBubblePos] = useState({ x: null, y: null });
  const dragRef = useRef(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const posStart = useRef({ x: 0, y: 0 });

  const handleDragStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    isDragging.current = false;
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    const rect = dragRef.current.getBoundingClientRect();
    posStart.current = { x: rect.left, y: rect.top };

    const handleMove = (ev) => {
      const t = ev.touches ? ev.touches[0] : ev;
      const dx = t.clientX - dragStart.current.x;
      const dy = t.clientY - dragStart.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) isDragging.current = true;
      if (isDragging.current) {
        ev.preventDefault();
        const newX = Math.max(0, Math.min(window.innerWidth - 64, posStart.current.x + dx));
        const newY = Math.max(0, Math.min(window.innerHeight - 64, posStart.current.y + dy));
        setBubblePos({ x: newX, y: newY });
      }
    };
    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove, { passive: false });
      document.removeEventListener('touchend', handleEnd);
      if (!isDragging.current) setIsOpen(true);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };
  const [quickSuggestions, setQuickSuggestions] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  
  const messagesEndRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (isOpen) {
      fetchFAQCategories();
      fetchQuickHelp();
      
      // Welcome message
      if (messages.length === 0) {
        setMessages([{
          type: 'assistant',
          text: "👋 Hi! I'm your PricePAL Compare Assistant. I can help with:\n\n• Product scanning & price comparison\n• Kitchen AI & meal planning\n• Account & subscription questions\n• Troubleshooting\n\nWhat can I help you with today?",
          timestamp: new Date()
        }]);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchFAQCategories = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/assistant/faq-categories`);
      setFaqCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching FAQ categories:', error);
    }
  };

  const fetchQuickHelp = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/assistant/quick-help?page=${currentPage || ''}`);
      setQuickSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching quick help:', error);
    }
  };

  const fetchCategoryFaqs = async (categoryKey) => {
    try {
      const response = await axios.get(`${backendUrl}/api/assistant/faq-category/${categoryKey}`);
      setCategoryFaqs(response.data.faqs);
      setSelectedCategory(response.data.category);
    } catch (error) {
      console.error('Error fetching category FAQs:', error);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      text: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Get AI response
      const response = await axios.post(`${backendUrl}/api/assistant/ask`, {
        user_id: userId,
        message: text,
        current_page: currentPage
      });

      // Add assistant response
      const assistantMessage = {
        type: 'assistant',
        text: response.data.response,
        suggestedFaqs: response.data.suggested_faqs || [],
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        type: 'assistant',
        text: "Sorry, I'm having trouble responding right now. Please try again or browse the FAQ section.",
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.action && suggestion.action.startsWith('faq:')) {
      // Direct to FAQ answer
      const faqId = suggestion.action.replace('faq:', '');
      sendMessage(suggestion.text);
    } else {
      sendMessage(suggestion.text);
    }
  };

  const submitFaqFeedback = async (faqId, helpful) => {
    try {
      await axios.post(`${backendUrl}/api/assistant/faq-feedback`, {
        faq_id: faqId,
        user_id: userId,
        helpful: helpful
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const toggleVoiceInput = () => {
    if (isRecording) {
      setIsRecording(false);
      // Stop recording logic
    } else {
      setIsRecording(true);
      // Start recording logic
      // For now, just show a placeholder
      setTimeout(() => {
        setIsRecording(false);
        setInputMessage('Voice input placeholder - implement with Web Speech API');
      }, 2000);
    }
  };

  // Floating button — draggable
  if (!isOpen) {
    const style = bubblePos.x !== null
      ? { position: 'fixed', left: bubblePos.x, top: bubblePos.y, zIndex: 50 }
      : { position: 'fixed', bottom: 24, right: 24, zIndex: 50 };
    return (
      <div ref={dragRef} style={style}>
        <button
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center text-2xl cursor-grab active:cursor-grabbing select-none"
          title="PricePAL Compare Assistant — drag to move"
          data-testid="pricepal-assistant-bubble"
        >
          💬
        </button>
      </div>
    );
  }

  // Chat widget
  return (
    <div
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-96 max-w-[24rem]"
      data-testid="pricepal-assistant-widget"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ height: 'min(600px, calc(100vh - 6rem))' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-3 py-3 sm:px-4 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-xl flex-shrink-0" aria-hidden="true">💬</span>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-sm leading-tight truncate">
                PricePAL Assistant
              </h3>
              <p className="text-[11px] opacity-90 truncate">Always here to help</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {activeTab === 'chat' && messages.length > 1 && (
              <button
                onClick={() => {
                  setMessages([{
                    type: 'assistant',
                    text: "👋 Hi! I'm your PricePAL Compare Assistant. I can help with:\n\n• Product scanning & price comparison\n• Kitchen AI & meal planning\n• Account & subscription questions\n• Troubleshooting\n\nWhat can I help you with today?",
                    timestamp: new Date()
                  }]);
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-md px-2 py-1 text-xs transition whitespace-nowrap"
                title="Clear Chat"
                data-testid="assistant-clear-btn"
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full w-7 h-7 flex items-center justify-center transition text-sm"
              aria-label="Close chat"
              data-testid="assistant-close-btn"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-2.5 text-sm font-medium transition ${
              activeTab === 'chat'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-blue-600'
            }`}
            data-testid="assistant-tab-chat"
          >
            💬 Chat
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`flex-1 py-2.5 text-sm font-medium transition ${
              activeTab === 'faq'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                : 'text-gray-600 hover:text-blue-600'
            }`}
            data-testid="assistant-tab-faq"
          >
            ❓ FAQ
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'chat' ? (
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      <div className="whitespace-pre-wrap text-sm">{message.text}</div>
                      
                      {/* Suggested FAQs */}
                      {message.suggestedFaqs && message.suggestedFaqs.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-300 space-y-2">
                          <div className="text-xs font-semibold">Related topics:</div>
                          {message.suggestedFaqs.map((faq, idx) => (
                            <button
                              key={idx}
                              onClick={() => sendMessage(faq.question)}
                              className="block w-full text-left text-xs bg-white bg-opacity-50 hover:bg-opacity-100 rounded p-2 transition"
                            >
                              {faq.category_icon} {faq.question}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Quick Suggestions */}
                {messages.length <= 1 && quickSuggestions.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-gray-500">Quick help:</div>
                    {quickSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="block w-full text-left bg-blue-50 hover:bg-blue-100 rounded-lg p-3 text-sm transition"
                      >
                        {suggestion.text}
                      </button>
                    ))}
                  </div>
                )}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 p-3 sm:p-4 bg-gray-50">
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage(inputMessage)}
                    placeholder="Ask me anything..."
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="assistant-input"
                  />
                  <button
                    onClick={toggleVoiceInput}
                    className={`w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center transition ${
                      isRecording
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    aria-label="Voice input"
                    data-testid="assistant-voice-btn"
                  >
                    🎤
                  </button>
                  <button
                    onClick={() => sendMessage(inputMessage)}
                    disabled={!inputMessage.trim() || isLoading}
                    className="px-3 py-2 flex-shrink-0 text-sm bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg hover:shadow-lg transition disabled:opacity-50"
                    data-testid="assistant-send-btn"
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto p-4">
              {!selectedCategory ? (
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-800 mb-4">Browse FAQ Topics</h4>
                  {faqCategories.map((category) => (
                    <button
                      key={category.key}
                      onClick={() => fetchCategoryFaqs(category.key)}
                      className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-3xl">{category.icon}</span>
                        <div>
                          <div className="font-semibold text-gray-800">{category.name}</div>
                          <div className="text-xs text-gray-500">{category.question_count} questions</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="mb-4 text-blue-600 hover:underline text-sm flex items-center"
                  >
                    ← Back to categories
                  </button>
                  <h4 className="font-bold text-gray-800 mb-4">
                    {selectedCategory.icon} {selectedCategory.name}
                  </h4>
                  <div className="space-y-4">
                    {categoryFaqs.map((faq) => (
                      <div key={faq.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="font-semibold text-gray-800 mb-2">{faq.question}</div>
                        <div className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{faq.answer}</div>
                        <div className="flex items-center space-x-2 text-xs">
                          <span className="text-gray-500">Was this helpful?</span>
                          <button
                            onClick={() => submitFaqFeedback(faq.id, true)}
                            className="text-green-600 hover:underline"
                          >
                            👍 Yes
                          </button>
                          <button
                            onClick={() => submitFaqFeedback(faq.id, false)}
                            className="text-red-600 hover:underline"
                          >
                            👎 No
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricePALAssistant;
